export interface NormalizeAttachmentNameOptions {
    lowerCase?: boolean;
    trimTrailingPunctuation?: boolean;
    trailingPunctuationPattern?: RegExp;
    trimTrailingPageSuffix?: boolean;
}

const DEFAULT_TRAILING_PUNCTUATION = /[.,;:]+$/;

export function safeDecodeURIComponent(value: string): string {
    try {
        return decodeURIComponent(value);
    } catch {
        return value;
    }
}

export function normalizeAttachmentName(value?: string, options: NormalizeAttachmentNameOptions = {}): string {
    if (!value) return "";

    const {
        lowerCase = true,
        trimTrailingPunctuation = false,
        trailingPunctuationPattern = DEFAULT_TRAILING_PUNCTUATION,
        trimTrailingPageSuffix = true
    } = options;

    let name = safeDecodeURIComponent(String(value)).trim();
    name = name.replace(/^attachments\//i, "");
    name = name.split("?")[0].split("#")[0];

    if (trimTrailingPageSuffix) {
        name = name.replace(/\s*\(?\b(?:page|pg|p)\.?\s*[:=]?\s*\d+\)?\s*$/i, "");
    }
    if (trimTrailingPunctuation) {
        name = name.replace(trailingPunctuationPattern, "");
    }

    const parts = name.split("/");
    name = (parts[parts.length - 1] ?? name).trim();
    return lowerCase ? name.toLowerCase() : name;
}

export function looksLikeAttachmentFileName(value: string): boolean {
    return /\.[a-z0-9]{1,10}$/i.test(value.trim());
}

export function stripLeadingBracketTokens(value: string): string {
    return value.replace(/^(?:\[\s*[\w.-]+\s*\]\s*)+/, "").trim();
}

export function getAttachmentFileExtension(value: string): string {
    const normalized = value.trim();
    const dotIndex = normalized.lastIndexOf(".");
    return dotIndex >= 0 ? normalized.slice(dotIndex).toLowerCase() : "";
}

export function normalizeLooseComparableAttachmentName(value?: string): string {
    if (!value) return "";
    const stripped = stripLeadingBracketTokens(value);
    return normalizeAttachmentName(stripped, {
        trimTrailingPunctuation: true
    });
}

export function isPartialAttachmentNameMatch(candidate: string, requested: string): boolean {
    const normalizedCandidate = normalizeLooseComparableAttachmentName(candidate);
    const normalizedRequested = normalizeLooseComparableAttachmentName(requested);
    if (!normalizedCandidate || !normalizedRequested) return false;
    if (normalizedCandidate === normalizedRequested) return true;

    const candidateExt = getAttachmentFileExtension(normalizedCandidate);
    const requestedExt = getAttachmentFileExtension(normalizedRequested);
    const candidateBase = candidateExt ? normalizedCandidate.slice(0, -candidateExt.length) : normalizedCandidate;
    const requestedBase = requestedExt ? normalizedRequested.slice(0, -requestedExt.length) : normalizedRequested;

    if (candidateBase && requestedBase && candidateBase === requestedBase) {
        return true;
    }

    return normalizedCandidate.includes(normalizedRequested) || normalizedRequested.includes(normalizedCandidate);
}

function collectAttachmentNamesFromText(value: string, sink: Set<string>): void {
    const text = safeDecodeURIComponent(value);
    const attachmentPathPattern = /attachments\/([^\]\n\r]+?\.[a-z0-9]{1,10})/gi;
    let match: RegExpExecArray | null = null;
    while ((match = attachmentPathPattern.exec(text)) !== null) {
        const normalized = normalizeAttachmentName(match[1], { trimTrailingPunctuation: true });
        if (normalized) sink.add(normalized);
    }
}

export function extractAttachmentNamesFromDataPoints(dataPoints: unknown): Set<string> {
    const names = new Set<string>();
    if (!Array.isArray(dataPoints)) return names;

    for (const point of dataPoints) {
        if (typeof point === "string") {
            collectAttachmentNamesFromText(point, names);
            continue;
        }

        if (!point || typeof point !== "object") {
            continue;
        }

        const item = point as Record<string, unknown>;
        const sourcePage = String(item.sourcepage_field ?? item.sourcePage ?? item.sourcepage ?? "").trim();
        const uploadDir = String(item.uploaddir_field ?? item.uploadDir ?? "").trim();
        if (sourcePage) {
            const normalizedFromFields = normalizeAttachmentName(uploadDir ? `${uploadDir}/${sourcePage}` : sourcePage, {
                trimTrailingPunctuation: true
            });
            if (normalizedFromFields) names.add(normalizedFromFields);
        }

        const fallbackFields = [
            item.content_field,
            item.path,
            item.source,
            item.sourcepage_field,
            item.sourcePage,
            item.sourcepage,
            item.file_name,
            item.fileName,
            item.filename,
            item.name
        ];
        for (const field of fallbackFields) {
            if (typeof field === "string") {
                collectAttachmentNamesFromText(field, names);
            }
        }
    }

    return names;
}
